import React, { useState } from 'react';
import { Tag, Input, Select, Row, Col, Space } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useVouchers } from '../../hooks/useVouchers';
import { DataTable } from '../../components';
import type { Voucher } from '../../services/voucher.service';

const VoucherList: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [typeFilter, setTypeFilter] = useState<string | undefined>();
  const [tenantFilter, setTenantFilter] = useState<string>('');

  const { vouchers, loading, meta, setFilters, refetch, filters } = useVouchers();

  const applyFilters = (overrides: Partial<typeof filters> = {}) => {
    setFilters({
      ...filters,
      page: 1,
      q: searchText || undefined,
      status: statusFilter,
      type: typeFilter,
      tenant_id: tenantFilter || undefined,
      ...overrides,
    });
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    applyFilters({ q: value || undefined });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setFilters({ ...filters, page, limit: pageSize });
  };

  const columns: ColumnsType<Voucher> = [
    {
      title: 'Mã',
      dataIndex: 'code',
      key: 'code',
      render: (code, record) => (
        <Space direction="vertical" size={0}>
          <a onClick={() => navigate(`/vouchers/${record.voucher_id}`)}>
            <code>{code}</code>
          </a>
          {record.source && <span style={{ fontSize: 11, color: '#8c8c8c' }}>{record.source}</span>}
        </Space>
      ),
    },
    {
      title: 'Loại',
      dataIndex: 'type',
      key: 'type',
      width: 130,
      render: (t: string) => <Tag>{t}</Tag>,
    },
    {
      title: 'Giá trị',
      dataIndex: 'value',
      key: 'value',
      width: 130,
      render: (v: number, r) => r.type === 'percentage' ? `${v}%` : v.toLocaleString('vi-VN'),
    },
    {
      title: 'Đã dùng',
      key: 'usage',
      width: 130,
      render: (_, r) => `${r.usage_count ?? 0}${r.usage_limit_total ? ' / ' + r.usage_limit_total : ''}`,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (s: string) => {
        const color = s === 'active' ? 'success' : s === 'expired' ? 'error' : 'default';
        return <Tag color={color}>{s}</Tag>;
      },
    },
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      width: 200,
      render: (id?: string) => id ? <code style={{ fontSize: 11 }}>{id.slice(-8)}</code> : '—',
    },
    {
      title: 'Hết hạn',
      dataIndex: 'end_date',
      key: 'end_date',
      width: 130,
      render: (date?: string) => date ? dayjs(date).format('DD/MM/YYYY') : '—',
    },
  ];

  const filterContent = (
    <Row gutter={16}>
      <Col span={8}>
        <Input.Search
          placeholder="Tenant ID"
          allowClear
          value={tenantFilter}
          onChange={(e) => setTenantFilter(e.target.value)}
          onSearch={() => applyFilters()}
        />
      </Col>
      <Col span={6}>
        <Select
          placeholder="Trạng thái"
          allowClear
          style={{ width: '100%' }}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); applyFilters({ status: v }); }}
          options={[
            { value: 'active', label: 'Active' },
            { value: 'expired', label: 'Expired' },
            { value: 'inactive', label: 'Inactive' },
          ]}
        />
      </Col>
      <Col span={6}>
        <Select
          placeholder="Loại"
          allowClear
          style={{ width: '100%' }}
          value={typeFilter}
          onChange={(v) => { setTypeFilter(v); applyFilters({ type: v }); }}
          options={[
            { value: 'fixed_amount', label: 'Fixed amount' },
            { value: 'percentage', label: 'Percentage' },
            { value: 'free_gift', label: 'Free gift' },
          ]}
        />
      </Col>
    </Row>
  );

  return (
    <DataTable<Voucher>
      title="Voucher (cross-tenant)"
      data={vouchers}
      columns={columns}
      loading={loading}
      rowKey="voucher_id"
      searchable
      searchPlaceholder="Mã / mô tả..."
      onSearch={handleSearch}
      onRefresh={refetch}
      filters={filterContent}
      pagination={{
        current: meta.page,
        pageSize: meta.limit,
        total: meta.total,
        onChange: handlePageChange,
      }}
      onRowClick={(record) => navigate(`/vouchers/${record.voucher_id}`)}
      actionColumn={{
        width: 80,
        fixed: 'right',
        actions: (record) => [
          {
            key: 'view',
            label: 'Xem chi tiết',
            icon: <EyeOutlined />,
            onClick: () => navigate(`/vouchers/${record.voucher_id}`),
          },
        ],
      }}
    />
  );
};

export default VoucherList;
export { VoucherList };
